import React from 'react';
import ChartContainer from '../../Common/Services/charts/apex-charts/ChartContainer';
import { coresMescladasArray } from '../../Common/styles/colors';
import { formatNumber } from '../Common/CensusMap';
import { CardsSkeleton, MapSkeleton, TableSkeleton } from '../Common/CensusSkeletons';
import { useCensusFiltersContext } from '../Common/Filters/hooks/useCensusFiltersContext';
import useCensusPopulationProjection from '../Common/hooks/useCensusPopulationProjection';
import ProjectionMap from './ProjectionMap';

// Censo e projeção são a mesma medida: mesma cor, a projeção se diferencia pelo traço tracejado.
const LINE_COLOR = coresMescladasArray[0];

function variationFrom(base, population) {
    if (population === null) return '—';
    const percent = (100 * (population - base)) / base;
    const sign = percent > 0 ? '+' : '';
    return `${sign}${percent.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function chartConfig({ series, base, currentYear }) {
    const census = series.filter(point => point.kind === 'census');
    // A linha da projeção sai do Censo 2022, para as duas linhas se encontrarem.
    const projection = [series.find(point => point.year === base.t2), ...series.filter(point => point.kind === 'projection')];
    // Começa na década do primeiro Censo (1991 -> 1990), para as marcas caírem em anos redondos.
    const firstYear = Math.floor(series[0].year / 10) * 10;
    const lastYear = series[series.length - 1].year;
    // Rótulos só em hoje e no último ano: o Censo 2022 já está nos destaques e ficaria encavalado com hoje.
    const labeledYears = [currentYear, lastYear];

    const toPoints = points => points.map(point => ({ x: point.year, y: point.population }));

    return {
        series: [
            { name: 'Censo', data: toPoints(census) },
            { name: 'Projeção', data: toPoints(projection) }
        ],
        options: {
            chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } },
            colors: [LINE_COLOR, LINE_COLOR],
            stroke: { width: 2, curve: 'straight', dashArray: [0, 6] },
            markers: { size: 5, strokeWidth: 2, strokeColors: '#fff', hover: { size: 7 } },
            dataLabels: {
                enabled: true,
                enabledOnSeries: [1],
                formatter: (value, { dataPointIndex, w }) => {
                    const year = w.config.series[1].data[dataPointIndex].x;
                    return labeledYears.includes(year) && value !== null ? formatNumber(value) : '';
                },
                offsetY: -8,
                background: { enabled: false },
                style: { fontSize: '12px', fontWeight: 600, colors: ['#252B37'] }
            },
            // Eixo proporcional ao tempo, com marcas de 10 em 10 anos (1990, 2000, ..., 2060).
            xaxis: {
                type: 'numeric',
                min: firstYear,
                max: lastYear,
                tickAmount: (lastYear - firstYear) / 10,
                labels: { formatter: value => String(Math.round(value)) },
                tooltip: { enabled: false }
            },
            yaxis: { labels: { formatter: value => formatNumber(Math.round(value)) } },
            // Margem à direita para o rótulo do último ano não ser cortado.
            grid: { borderColor: '#EEF0F2', strokeDashArray: 3, padding: { right: 32, left: 8 } },
            legend: { show: false },
            tooltip: {
                shared: true,
                x: { formatter: value => String(value) },
                y: { formatter: value => (value === null || value === undefined ? '—' : `${formatNumber(value)} pessoas`) }
            }
        }
    };
}

const PopulationProjection = () => {
    const { filters } = useCensusFiltersContext();
    const cityId = filters.cidade?.value;
    const { data: projection, isPending, isError, error } = useCensusPopulationProjection(cityId);

    if (!cityId) {
        return <div className="censo-notice">Selecione uma cidade para ver a projeção populacional.</div>;
    }
    if (isError) {
        return <div className="censo-notice censo-notice-error">{error.message}</div>;
    }
    if (isPending) {
        return (
            <>
                <CardsSkeleton count={3} />
                <MapSkeleton />
                <div className="censo-projection-body">
                    <TableSkeleton rows={7} />
                </div>
            </>
        );
    }
    if (!projection) {
        return (
            <div className="censo-notice">
                Não é possível projetar a população deste município: ele só tem população no Censo 2022 (foi
                criado depois de 2010).
            </div>
        );
    }

    const { base, currentYear, series } = projection;
    const now = series.find(point => point.year === currentYear);
    const last = series[series.length - 1];
    const firstInvalid = series.find(point => point.population === null);
    const { series: chartSeries, options } = chartConfig(projection);

    const highlights = [
        { label: `Censo ${base.t2} (retrato)`, value: base.p2 },
        { label: `Hoje (${currentYear}, projeção)`, value: now ? now.population : null },
        { label: `${last.year} (projeção)`, value: last.population }
    ];

    return (
        <>
            <div className="censo-panorama">
                {highlights.map(({ label, value }) => (
                    <div key={label} className="censo-card">
                        <span>{label}</span>
                        <strong>{formatNumber(value)}</strong>
                    </div>
                ))}
            </div>

            <section className="censo-projection-area" aria-labelledby="censo-projection-area-title">
                <h6 id="censo-projection-area-title">Projeção por área</h6>
                <p className="censo-panel-sub">
                    Cada área recebe a projeção do município na proporção da sua população no Censo {base.t2}. Por
                    isso todas crescem no mesmo ritmo e as cores do mapa são as mesmas em qualquer ano; mudam os
                    números de cada área.
                </p>
            </section>
            <ProjectionMap projection={projection} />

            <div className="censo-projection-body">
                <div className="censo-projection-chart">
                    <ChartContainer title="População residente" options={options} series={chartSeries} type="line" height={340} />
                </div>

                <section>
                    <h5>Valores</h5>
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th scope="col">Ano</th>
                                <th scope="col" className="text-right">População</th>
                                <th scope="col" className="text-right">vs. {base.t2}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {series.map(point => (
                                <tr key={point.year}>
                                    <td>
                                        {point.year}{' '}
                                        <span className="censo-panel-sub">
                                            {point.kind === 'census' ? 'Censo' : 'Projeção'}
                                        </span>
                                    </td>
                                    <td className="text-right">{formatNumber(point.population)}</td>
                                    <td className="text-right">{variationFrom(base.p2, point.population)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            </div>

            {firstInvalid && (
                <div className="censo-notice">
                    Como a população caiu entre os Censos, a partir de {firstInvalid.year} a reta chegaria a zero ou
                    menos; esses anos aparecem como "—".
                </div>
            )}
        </>
    );
};

export default PopulationProjection;
